/**
 * @fileoverview A location's photo strip, the way usePlantPhotos is a plant's: which
 * picture is in view, the words on it, and the strip updated the moment a
 * photo arrives or goes, before the list has refetched.
 *
 * Where: LocationCard.
 * API: none; it loads the photo in view's card-size image to know when it
 * has arrived.
 * isLoading is false with no photos: there is no picture to wait for.
 *
 * @module useLocationPhotos
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Location } from '@/shared/api/locations';
import type { PlantPhoto } from '@/shared/types/photo';
import type { PlantPhotos } from '@/features/plant-list/lib/usePlantPhotos';
import { photoUrl } from '@/features/plant-list/lib/plantPhotoUrl';

/**
 * The photo strip for one location: its photos, the one in view, and the
 * note written with it.
 *
 * A photo the gardener has just added or changed is "pinned": when the list
 * refetches, the strip stays on that photo rather than on whatever index it
 * was showing, which may now be a different picture.
 *
 * @param location - The location as the list has it; its photos and notes seed the strip.
 * @returns The same shape usePlantPhotos gives a plant card.
 */
export function useLocationPhotos(location: Location): PlantPhotos {
  const [photos, setPhotos] = useState<PlantPhoto[]>(() => location.photos ?? []);
  const [selected, setSelected] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  /** The strip as it is now, for the callbacks without making them change each render. */
  const photosRef = useRef(photos);
  photosRef.current = photos;
  /** The photo just added or changed here, to stay on through the next refetch. */
  const pinnedId = useRef<number | null>(null);

  // The list's copy, when it arrives: keep the picture in view where it was.
  useEffect(() => {
    const next = location.photos ?? [];
    setSelected((current) => {
      const pinned = pinnedId.current === null ? -1 : next.findIndex((photo) => photo.photo_id === pinnedId.current);
      if (pinned >= 0) {
        if (next !== photosRef.current) pinnedId.current = null;
        return pinned;
      }
      const showing = photosRef.current[current];
      if (!showing) return 0;
      const stillThere = next.findIndex((photo) => photo.photo_id === showing.photo_id);
      return stillThere >= 0 ? stillThere : 0;
    });
    setPhotos(next);
  }, [location.photos]);

  // The picture in view is loading until its card-size image has arrived. A
  // picture left before it arrived must not mark the next one loaded.
  useEffect(() => {
    if (photos.length === 0 || !photos[selected]) return;
    const image = new Image();
    image.onload = () => setIsLoading(false);
    image.onerror = () => setIsLoading(false);
    image.src = photoUrl(photos[selected], 'card');
    setIsLoading(true);
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [selected, photos]);

  /** A photo added or changed here: shown at once, and pinned through the next refetch. */
  const received = useCallback((photo: PlantPhoto) => {
    const current = photosRef.current;
    const at = current.findIndex((existing) => existing.photo_id === photo.photo_id);
    let next: PlantPhoto[];
    if (at < 0) {
      next = [photo, ...current];
      setSelected(0);
    } else {
      next = [...current];
      next[at] = { ...current[at], ...photo };
      setSelected(at);
    }
    pinnedId.current = photo.photo_id;
    photosRef.current = next;
    setPhotos(next);
    setIsLoading(true);
  }, []);

  /** A photo deleted here: gone from the strip at once, the view kept in range. */
  const removed = useCallback((photoId: number) => {
    const next = photosRef.current.filter((p) => p.photo_id !== photoId);
    setSelected((at) => (at >= next.length ? Math.max(0, next.length - 1) : at));
    photosRef.current = next;
    setPhotos(next);
  }, []);

  /** The words of the note written with the photo in view, if it has one. */
  const note = useMemo(() => {
    const noteId = photos[selected]?.note_id;
    if (!noteId) return null;
    return location.notes?.find((n) => n.note_id === noteId)?.content ?? null;
  }, [photos, selected, location.notes]);

  // Nothing to wait for without a photo; the state is left as it was for the next one.
  return { photos, selected, select: setSelected, current: photos[selected], isLoading: photos.length > 0 && isLoading, received, removed, note };
}
